export gl = null

export setContext = (ctx) -> gl = ctx

glTypeSize = (t) -> switch t
	when gl.BYTE then 1
	when gl.UNSIGNED_BYTE then 1
	when gl.SHORT then 2
	when gl.UNSIGNED_SHORT then 2
	when gl.FLOAT then 4
	else 0

export class MeshSet
	constructor: (@attribs, verts, indices, @parts) ->
		@stride = 0
		for attr in @attribs
			attr.count ?= 1
			attr.type ?= gl.FLOAT
			attr.offset ?= @stride
			attr.norm ?= false
			@stride += attr.count * glTypeSize attr.type
		@verts = gl.createBuffer()
		@setVerts verts, gl.STATIC_DRAW if verts
		@setIndices indices, gl.STATIC_DRAW if indices

	clear: ->
		gl.deleteBuffer @verts
		gl.deleteBuffer @indices

	use: ->
		gl.bindBuffer gl.ARRAY_BUFFER, @verts if @verts
		gl.bindBuffer gl.ELEMENT_ARRAY_BUFFER, @indices if @indices
		for attr, loc in @attribs
			gl.enableVertexAttribArray loc
			gl.vertexAttribPointer loc, attr.count, attr.type, attr.norm, @stride, attr.offset
		if @attribs.length < 3
			gl.disableVertexAttribArray 2

	setVerts: (verts, type) ->
		gl.bindBuffer gl.ARRAY_BUFFER, @verts
		gl.bufferData gl.ARRAY_BUFFER, verts, type or gl.DYNAMIC_DRAW
		@vertsCount = verts.byteLength/@stride

	setIndices: (indices, type) ->
		gl.bindBuffer gl.ELEMENT_ARRAY_BUFFER, @indices or= gl.createBuffer()
		gl.bufferData gl.ELEMENT_ARRAY_BUFFER, indices, type or gl.DYNAMIC_DRAW
		@indicesCount = indices.byteLength/2

	draw: (i, n, mode) ->
		if @indices
			gl.drawElements mode or gl.TRIANGLES, n or @indicesCount, gl.UNSIGNED_SHORT, i or 0
		else
			gl.drawArrays mode or gl.TRIANGLES, i or 0, n or @vertsCount

	drawPart: (i) -> gl.drawElements gl.TRIANGLES, @parts[i].count, gl.UNSIGNED_SHORT, @parts[i].first*2

	@compose: (meshes) ->
		vertsCount = 0
		indicesCount = 0
		for mesh in meshes
			vertsCount += mesh.verts.length
			indicesCount += mesh.faces.length*3
		i16 = new Int16Array vertsCount*6
		indices = new Uint16Array indicesCount
		curVert = 0
		curFace = 0
		parts = []
		for mesh in meshes
			parts.push {first: curFace*3, count: mesh.faces.length*3}
			for face in mesh.faces
				indices.set face.map((index) -> curVert + index), curFace*3
				curFace += 1
			for vert in mesh.verts
				i16.set vert.map((v) -> Math.trunc v*0x7fff), curVert*6
				curVert += 1
		new MeshSet [{count: 3, type: gl.SHORT, norm: true}, {count: 3, type: gl.SHORT, norm: true}], i16, indices, parts

export class Program
	constructor: (source, uniforms = {}) ->
		attribs = for attr in (source.match /^attribute.+$/gm)
			attr.match(/attribute\s+\S+\s+(\S+);/)[1]
		for u in (source.match /^uniform.+$/gm)
			[,, name,, value] = u.match(/uniform\s+(\S+)\s+(\S+);(\s*\/\/\s*(.*))?/)
			if value?
				value = value.split(',').map (v) -> +v
				uniforms[name] ?= value
		loadShader = (type, source) ->
			shader = gl.createShader type
			gl.shaderSource shader, source
			gl.compileShader shader
			if not gl.getShaderParameter shader, gl.COMPILE_STATUS
				console.error gl.getShaderInfoLog shader
			shader
		@vertexShader = loadShader gl.VERTEX_SHADER, '#define VERT\n' + source
		@fragmentShader = loadShader gl.FRAGMENT_SHADER, source
		@id = gl.createProgram()
		gl.attachShader @id, @vertexShader
		gl.attachShader @id, @fragmentShader
		gl.bindAttribLocation @id, loc, name for name, loc in attribs
		gl.linkProgram @id
		if not gl.getProgramParameter @id, gl.LINK_STATUS
			console.error gl.getProgramInfoLog @id
		@uniforms = {}
		@use uniforms if uniforms

	clear: ->
		gl.deleteProgram @id
		gl.deleteShader @vertexShader
		gl.deleteShader @fragmentShader

	setUniforms: (uniforms) ->
		tex = 0
		for name, value of uniforms
			loc = @uniforms[name] = @uniforms[name] or gl.getUniformLocation @id, name
			if loc
				if value instanceof Texture
					gl.uniform1i loc, value.bind(tex)
					tex += 1
				else switch value.length ? null
					when 1 then gl.uniform1fv loc, new Float32Array value
					when 2 then gl.uniform2fv loc, new Float32Array value
					when 3 then gl.uniform3fv loc, new Float32Array value
					when 4 then gl.uniform4fv loc, new Float32Array value
					when 9 then gl.uniformMatrix3fv loc, false, new Float32Array value
					when 16 then gl.uniformMatrix4fv loc, false, new Float32Array value
					when null then gl.uniform1f loc, value
					else console.error "Unsupported uniform value: #{value}"
			else
				console.error "Missing uniform: #{name}"

	use: (uniforms = {}) ->
		gl.useProgram @id
		@setUniforms uniforms
		@

	draw: (buffer, uniforms = {}, mode = gl.TRIANGLES, i, n) ->
		@use uniforms
		buffer.use @attribs
		buffer.draw i, n, mode

export class Texture
	constructor: (params = {}, draw) ->
		@id = gl.createTexture()
		@set params
		@drawTo draw if draw

	clear: -> gl.deleteTexture @id

	set: (params) ->
		@format = params.format or gl.RGBA
		@type = params.type or gl.UNSIGNED_BYTE
		@magFilter = params.filter or params.magFilter or gl.LINEAR
		@minFilter = params.filter or params.minFilter or gl.LINEAR
		@bind()
		gl.texParameteri gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, @magFilter
		gl.texParameteri gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, @minFilter
		gl.texParameteri gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, params.wrap or params.wrapS or gl.CLAMP_TO_EDGE
		gl.texParameteri gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, params.wrap or params.wrapT or gl.CLAMP_TO_EDGE
		if params.width and params.height
			@width = params.width
			@height = params.height
			@setData params.data

	setData: (data) ->
		@bind()
		gl.texImage2D gl.TEXTURE_2D, 0, @format, @width, @height, 0, @format, @type, data or null

	setImage: (image) ->
		@width = if image then image.width else @width
		@height = if image then image.height else @height
		@bind()
		gl.texImage2D gl.TEXTURE_2D, 0, @format, @format, @type, image or null
		gl.generateMipmap gl.TEXTURE_2D if @minFilter and @minFilter != gl.NEAREST and @minFilter != gl.LINEAR

	@firstFreeSlot: 0

	lockTextureSlot: ->
		@slot = @bind()
		Texture.firstFreeSlot += 1

	bind: (i) ->
		return @slot if @slot?
		slot = Texture.firstFreeSlot + (i or 0)
		gl.activeTexture gl.TEXTURE0 + slot
		gl.bindTexture gl.TEXTURE_2D, @id
		slot

	drawTo: (draw) ->
		framebuffer = gl.createFramebuffer()
		renderbuffer = gl.createRenderbuffer()
		gl.bindFramebuffer gl.FRAMEBUFFER, framebuffer
		gl.bindRenderbuffer gl.RENDERBUFFER, renderbuffer
		if @width != renderbuffer.width or @height != renderbuffer.height
			renderbuffer.width = @width
			renderbuffer.height = @height
		gl.renderbufferStorage gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, @width, @height
		gl.framebufferTexture2D gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, @id, 0
		gl.framebufferRenderbuffer gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, renderbuffer
		if gl.checkFramebufferStatus(gl.FRAMEBUFFER) != gl.FRAMEBUFFER_COMPLETE
			return false
		prevViewport = gl.getParameter gl.VIEWPORT
		gl.viewport 0, 0, @width, @height
		draw()
		gl.bindFramebuffer gl.FRAMEBUFFER, null
		gl.deleteFramebuffer framebuffer
		gl.generateMipmap gl.TEXTURE_2D if @minFilter and @minFilter != gl.NEAREST and @minFilter != gl.LINEAR
		gl.bindRenderbuffer gl.RENDERBUFFER, null
		gl.deleteRenderbuffer renderbuffer
		gl.viewport prevViewport[0], prevViewport[1], prevViewport[2], prevViewport[3]
		true

	toCanvas: ->
		framebuffer = gl.createFramebuffer()
		gl.bindFramebuffer gl.FRAMEBUFFER, framebuffer
		gl.framebufferTexture2D gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, @id, 0
		data = new Uint8Array @width * @height * 4
		gl.readPixels 0, 0, @width, @height, @format, gl.UNSIGNED_BYTE, data
		gl.bindFramebuffer gl.FRAMEBUFFER, null
		gl.deleteFramebuffer framebuffer
		canvas = document.createElement 'canvas'
		canvas.width = @width
		canvas.height = @height
		context = canvas.getContext '2d'
		imageData = context.createImageData @width, @height
		imageData.data.set data
		context.putImageData imageData, 0, 0
		canvas

	swap: (other) ->
		temp = other.id
		other.id = @id
		@id = temp
		temp = other.width
		other.width = @width
		@width = temp
		temp = other.height
		other.height = @height
		@height = temp

	@load: (path, options = {}, onload) ->
		texture = new Texture options
		img = new Image()
		img.src = path
		img.onload = ->
			texture.setImage img
			onload? texture
		texture

export drawIcon = (width, height, draw) ->
	tex = new Texture {width, height}
	tex.drawTo draw
	c = tex.toCanvas()
	tex.clear()
	c



// WEBPACK FOOTER //
// ./client/gl.coffee